import * as React from 'react';
import type { ReactNode } from 'react';
import { tabbable } from 'tabbable';
import { Key } from 'ts-keycode-enum';
import { scrollToElement } from '~/Partials/Utils/utils';

interface TabListProps {
    children: ReactNode;
    name: string;
    id?: string;
    className?: string;
    ref?: React.Ref<HTMLDivElement>;
}

const handleFocusChange = (event: React.KeyboardEvent, element: HTMLElement) => {
    element.focus();
    scrollToElement(element, 'smooth', 'nearest', 'center');

    // Disable default scroll behavior on arrow keys
    event.preventDefault();
};

export const TabList = React.forwardRef<HTMLDivElement, React.PropsWithChildren<TabListProps>>((props, ref) => {
    const { children, name, id, className } = props;

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.keyCode === Key.LeftArrow || e.keyCode === Key.RightArrow) {
            if (typeof ref !== 'object' || ref === null) {
                throw new Error('Callback refs are not supported.');
            }

            if (!ref.current) {
                return;
            }

            const tabbableDescendants = tabbable(ref.current);
            const currentFocusedElement = tabbableDescendants.find(el => el === document.activeElement);

            if (currentFocusedElement && tabbableDescendants.length > 1) {
                if (e.keyCode === Key.LeftArrow) {
                    if (currentFocusedElement.previousSibling) {
                        handleFocusChange(e, currentFocusedElement.previousSibling as HTMLElement);
                    } else {
                        handleFocusChange(e, tabbableDescendants[tabbableDescendants.length - 1] as HTMLElement);
                    }
                } else if (e.keyCode === Key.RightArrow) {
                    if (currentFocusedElement.nextSibling) {
                        handleFocusChange(e, currentFocusedElement.nextSibling as HTMLElement);
                    } else {
                        handleFocusChange(e, tabbableDescendants[0] as HTMLElement);
                    }
                }
            }
        }
    };

    return (
        // eslint-disable-next-line jsx-a11y/interactive-supports-focus
        <div role="tablist" id={id} ref={ref} className={className} onKeyDown={handleKeyDown} aria-label={name}>
            {children}
        </div>
    );
});
